/**
 * Shared image processing utilities
 */

import { compressImage, CompressionPresets } from './imageCompression';
import { applyWatermark, WatermarkOptions } from './watermark';
import { adjustContrast, ContrastOptions } from './contrastAdjustment';
import { adjustWhiteBalance, WhiteBalanceOptions } from './whiteBalance';

export interface ImageProcessingOptions {
  // Watermark settings
  enableWatermark?: boolean;
  watermarkOptions?: WatermarkOptions;
  
  // Compression settings
  enableCompression?: boolean;
  compressionTargetSize?: number;
  compressionMinSize?: number;
  
  // Contrast adjustment settings
  enableContrastAdjustment?: boolean;
  contrastOptions?: ContrastOptions;

  // White balance settings
  enableWhiteBalance?: boolean;
  whiteBalanceOptions?: WhiteBalanceOptions;

  // Progress callback
  onProgress?: (progress: number) => void;
}

export interface ImageProcessingResult {
  blob: Blob;
  fileSize: number;
  originalSize: number;
  appliedFeatures: string[];
  url?: string;
}

/**
 * Process an image with watermark and/or compression
 * This is the main entry point for image processing
 */
export async function processImage(
  file: File,
  options: ImageProcessingOptions
): Promise<ImageProcessingResult> {
  const appliedFeatures: string[] = [];
  let processedBlob: Blob = file;
  let fileSize = file.size;
  const originalSize = file.size;
  
  // Track progress through stages
  let currentProgress = 0;
  const contrastProgress = options.enableContrastAdjustment ? 20 : 0;
  const whiteBalanceProgress = options.enableWhiteBalance ? 20 : 0;
  const watermarkProgress = options.enableWatermark ? 30 : 0;
  const compressionProgress = options.enableCompression ? 30 : 0;
  const totalProgress = contrastProgress + whiteBalanceProgress + watermarkProgress + compressionProgress || 100;

  // Stage 1: Apply contrast adjustment if enabled
  if (options.enableContrastAdjustment && options.contrastOptions) {
    try {
      options.onProgress?.(currentProgress);

      const contrastResult = await adjustContrast(processedBlob, options.contrastOptions);

      processedBlob = contrastResult.blob;
      fileSize = processedBlob.size;
      appliedFeatures.push('contrastAdjustment');

      currentProgress += contrastProgress;
      options.onProgress?.(currentProgress * 100 / totalProgress);
    } catch (error) {
      console.error('Contrast adjustment failed:', error);
      throw new Error('Failed to apply contrast adjustment');
    }
  }

  // Stage 2: Apply white balance if enabled
  if (options.enableWhiteBalance && options.whiteBalanceOptions) {
    try {
      const whiteBalanceResult = await adjustWhiteBalance(processedBlob, options.whiteBalanceOptions);

      processedBlob = whiteBalanceResult.blob;
      fileSize = processedBlob.size;
      appliedFeatures.push('whiteBalance');

      currentProgress += whiteBalanceProgress;
      options.onProgress?.(currentProgress * 100 / totalProgress);
    } catch (error) {
      console.error('White balance adjustment failed:', error);
      throw new Error('Failed to apply white balance');
    }
  }
  
  // Stage 3: Apply watermark if enabled
  if (options.enableWatermark && options.watermarkOptions) {
    try {
      options.onProgress?.(currentProgress);
      
      // For non-compressed images, pass original size for quality adjustment
      const originalSizeMB = !options.enableCompression ? file.size / (1024 * 1024) : undefined;
      
      const watermarkResult = await applyWatermark(
        processedBlob,
        options.watermarkOptions,
        originalSizeMB
      );
      
      processedBlob = watermarkResult.blob;
      fileSize = watermarkResult.blob.size;
      appliedFeatures.push('watermark');
      
      currentProgress += watermarkProgress;
      options.onProgress?.(currentProgress * 100 / totalProgress);
    } catch (error) {
      console.error('Watermark application failed:', error);
      throw new Error('Failed to apply watermark');
    }
  }
  
  // Stage 4: Auto-compress if watermark made file > 5MB (and compression is OFF)
  if (options.enableWatermark && !options.enableCompression && fileSize > CompressionPresets.FIVE_MB) {
    try {
      const tempFile = new File([processedBlob], file.name, { type: processedBlob.type || 'image/jpeg' });
      
      const compressionResult = await compressImage(tempFile, {
        targetSize: CompressionPresets.FIVE_MB * 0.98, // Target 4.9MB for safety
        format: 'jpeg',
        resizeEnabled: false,
        initialQuality: 0.9,
        onProgress: (compressProgress) => {
          const progress = currentProgress + (compressProgress * 20 / 100);
          options.onProgress?.(progress * 100 / totalProgress);
        }
      });
      
      processedBlob = compressionResult.blob;
      fileSize = compressionResult.compressedSize;
      
      // Double-check: ensure it's really under 5MB
      if (fileSize > CompressionPresets.FIVE_MB) {
        const strongCompressionResult = await compressImage(tempFile, {
          targetSize: CompressionPresets.FIVE_MB * 0.9, // Target 4.5MB
          format: 'jpeg',
          resizeEnabled: true,
          initialQuality: 0.7,
          onProgress: (compressProgress) => {
            const progress = currentProgress + 20 + (compressProgress * 10 / 100);
            options.onProgress?.(progress * 100 / totalProgress);
          }
        });
        
        processedBlob = strongCompressionResult.blob;
        fileSize = strongCompressionResult.compressedSize;
      }
      
      appliedFeatures.push('auto-compression-5mb');
      currentProgress += 30;
      options.onProgress?.(currentProgress * 100 / totalProgress);
    } catch (error) {
      console.error('Auto compression failed:', error);
      throw new Error('Failed to auto-compress after watermark');
    }
  }
  
  // Stage 5: Apply compression if enabled
  if (options.enableCompression && options.compressionTargetSize) {
    try {
      // Skip if already smaller than target
      if (fileSize >= options.compressionTargetSize) {
        const tempFile = processedBlob instanceof File 
          ? processedBlob 
          : new File([processedBlob], file.name, { type: processedBlob.type || 'image/jpeg' });
        
        const compressionResult = await compressImage(tempFile, {
          targetSize: options.compressionTargetSize,
          format: 'jpeg',
          resizeEnabled: false,
          minSize: options.compressionMinSize,
          onProgress: (compressProgress) => {
            const progress = currentProgress + (compressProgress * compressionProgress / 100);
            options.onProgress?.(progress * 100 / totalProgress);
          }
        });
        
        processedBlob = compressionResult.blob;
        fileSize = compressionResult.compressedSize;
        appliedFeatures.push('compression');
      }
      
      currentProgress += compressionProgress;
      options.onProgress?.(currentProgress * 100 / totalProgress);
    } catch (error) {
      console.error('Compression failed:', error);
      throw new Error('Failed to compress image');
    }
  }
  
  // Generate data URL
  const reader = new FileReader();
  const url = await new Promise<string>((resolve, reject) => {
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(processedBlob);
  });
  
  options.onProgress?.(100);
  
  return {
    blob: processedBlob,
    fileSize,
    originalSize,
    appliedFeatures,
    url
  };
}

/**
 * Process multiple images in batch
 */
export async function processImageBatch(
  files: File[],
  options: ImageProcessingOptions,
  onBatchProgress?: (current: number, total: number) => void
): Promise<ImageProcessingResult[]> {
  const results: ImageProcessingResult[] = [];
  
  for (let i = 0; i < files.length; i++) {
    onBatchProgress?.(i, files.length);
    
    const result = await processImage(files[i], {
      ...options,
      onProgress: (progress) => {
        const batchProgress = (i + progress / 100) / files.length * 100;
        options.onProgress?.(batchProgress);
      }
    });
    
    results.push(result);
  }
  
  onBatchProgress?.(files.length, files.length);
  return results;
}